import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SCREEN_SIZE } from 'types';
import { selectScreenSize } from 'redux/session/selectors';
import {
  Button,
  TextInput,
  Form,
  InlineLoading
} from 'carbon-components-react';
import { ArrowRight16, ArrowLeft16 } from '@carbon/icons-react';
import { useFormik } from 'formik';
import { FormError } from 'components';
import Utils from 'utilities/helper.util';
import * as Yup from 'yup';

interface ElevenHundredDetailsFormValues {
  dybdUsername: string;
}

const Schema = Yup.object().shape({
  dybdUsername: Yup.string().label('BYDA username').required().email()
});

const ElevenHundredDetails: React.FC<Props> = ({
  onSubmit,
  onClickBack,
  elevenHundredDetailsValues,
  isRequestingUserLinking,
  onClickCancel,
  requestUserLinkingError
}) => {
  const screenSize = useSelector(selectScreenSize);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);
  const [showAPIError, setShowAPIError] = useState<boolean>(false);

  const {
    touched,
    errors,
    values,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleBlur
  } = useFormik<ElevenHundredDetailsFormValues>({
    enableReinitialize: true,
    initialValues: {
      dybdUsername: elevenHundredDetailsValues
    },
    validationSchema: Schema,
    onSubmit: (values: ElevenHundredDetailsFormValues) => {
      onSubmit(values);
    }
  });

  const handleTextInputChange = (key: string, val: string) => {
    setFieldTouched(key, true, true);
    setFieldValue(key, val);
    setShowAPIError(false);
  };

  useEffect(() => {
    if (Utils.isObjectEmpty(errors) && values.dybdUsername) {
      setNextButtonDisabled(false);
    } else {
      setNextButtonDisabled(true);
    }
  }, [errors, touched, values]);

  useEffect(() => {
    if (requestUserLinkingError) {
      setShowAPIError(true);
    } else {
      setShowAPIError(false);
    }
  }, [requestUserLinkingError]);
  return (
    <Form onSubmit={handleSubmit}>
      <div className="form-fields-wrapper">
        <div className={`textinput-fields-wrapper padding`}>
          <div id="dbydUsernameSection" className="form-fields step2">
            <TextInput
              id="dybdUsername"
              name="dybdUsername"
              className="custom-fields-styling"
              labelText="BYDA username"
              placeholder="required"
              onChange={(val) =>
                handleTextInputChange('dybdUsername', val.target.value)
              }
              onBlur={handleBlur}
              value={values.dybdUsername}
              size={'lg'}
              maxLength={200}
            />
            {errors.dybdUsername && touched.dybdUsername && (
              <FormError>{errors.dybdUsername}</FormError>
            )}
            <div className="custom-helper-text">
              {`We use this information to fetch, display and lodge enquiries in SmarterWX Locate. 
                  A verification code will be sent to this email address in the next step.`}
            </div>
            {requestUserLinkingError && showAPIError && (
              <div className="api-error-message">
                <FormError>{requestUserLinkingError}</FormError>
              </div>
            )}
          </div>
        </div>
      </div>
      <div id="formButtons" className="form-buttons-wrapper-step2  padding">
        {screenSize !== SCREEN_SIZE.SM && (
          <Button
            kind="tertiary"
            size={'md'}
            renderIcon={ArrowLeft16}
            hasIconOnly={true}
            className="back-button"
            onClick={() => onClickBack()}
            iconDescription="Back"
            disabled={isRequestingUserLinking}
          />
        )}
        <Button
          kind="tertiary"
          size={'md'}
          className="cancel-button"
          disabled={isRequestingUserLinking}
          onClick={() => onClickCancel()}
        >
          Cancel
        </Button>
        <Button
          kind="secondary"
          type="submit"
          size={'md'}
          renderIcon={isRequestingUserLinking ? '' : ArrowRight16}
          className="next-button"
          disabled={nextButtonDisabled || isRequestingUserLinking}
        >
          {!isRequestingUserLinking ? (
            'Next'
          ) : (
            <InlineLoading
              description={!isRequestingUserLinking ? 'Next' : 'Submitting...'}
              status={!isRequestingUserLinking ? 'finished' : 'active'}
              aria-live={'assertive'}
            />
          )}
        </Button>
      </div>
    </Form>
  );
};

type Props = {
  onSubmit: Function;
  onClickBack: Function;
  elevenHundredDetailsValues: string;
  isRequestingUserLinking?: boolean;
  onClickCancel: Function;
  requestUserLinkingError?: any;
};

export default React.memo(ElevenHundredDetails);
